import React, { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";
import { CloseCircle, TickCircle, Award } from "iconsax-react";
import confetti from "canvas-confetti";
import { Lesson } from "../../../services/lessonsService";

import MobileLayout from "../../../components/MobileLayout";
import { fontSizes, lineHeights } from "../../../shared/styles/typography";

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const ProgressTrack = styled.div`
  flex: 1;
  height: 12px;
  background: #f3f4f6;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
`;

const ProgressBar = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${(props) => props.$progress}%;
  background: linear-gradient(90deg, #5f33e1, #8b5cf6);
  transition: width 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 0.5rem;
  transition: color 0.2s;
  &:hover {
    color: #555;
  }
`;

const ExerciseContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const QuestionBox = styled.div`
  text-align: center;
  h2 {
    font-size: ${fontSizes.xl}px;
    line-height: ${lineHeights.xl}px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 0.5rem;
  }
  p {
    color: #666;
    font-size: ${fontSizes.base}px;
    line-height: ${lineHeights.base}px;
  }
`;

const MediaWrapper = styled.div`
  width: 100%;
  max-width: 500px;
  aspect-ratio: 16/9;
  margin: 0 auto;
  border-radius: 1.5rem;
  overflow: hidden;
  background: black;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  position: relative;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const OptionButton = styled.button<{
  $status?: "selected" | "correct" | "wrong" | "idle";
}>`
  background: white;
  border: 3px solid #f3f4f6;
  border-radius: 1.25rem;
  padding: 1.5rem 1rem;
  font-size: ${fontSizes.base}px;
  line-height: ${lineHeights.base}px;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 0 #f3f4f6;

  &:hover {
    ${(props) =>
      props.$status === "idle" &&
      css`
        border-color: #5f33e180;
        background: #5f33e105;
        transform: translateY(-2px);
        box-shadow: 0 6px 0 #5f33e120;
      `}
  }

  &:active {
    transform: translateY(2px);
    box-shadow: 0 0 0 transparent;
  }

  ${(props) =>
    props.$status === "selected" &&
    css`
      border-color: #5f33e1;
      background: #5f33e110;
      color: #5f33e1;
      box-shadow: 0 4px 0 #5f33e140;
    `}

  ${(props) =>
    props.$status === "correct" &&
    css`
      border-color: #10b981;
      background: #10b98110;
      color: #10b981;
      box-shadow: 0 4px 0 #10b98140;
    `}

  ${(props) =>
    props.$status === "wrong" &&
    css`
      border-color: #ef4444;
      background: #ef444410;
      color: #ef4444;
      box-shadow: 0 4px 0 #ef444440;
    `}
`;

const Footer = styled.div<{ $status?: "correct" | "wrong" | "idle" }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.5rem;
  background: white;
  border-top: 2px solid #f3f4f6;
  display: flex;
  justify-content: center;
  transition: background 0.3s ease;

  ${(props) =>
    props.$status === "correct" &&
    css`
      background: #ecfdf5;
      border-top-color: #10b981;
    `}
  ${(props) =>
    props.$status === "wrong" &&
    css`
      background: #fef2f2;
      border-top-color: #ef4444;
    `}
`;

const FooterContent = styled.div`
  max-width: 800px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;

  .feedback {
    display: flex;
    align-items: center;
    gap: 1rem;

    .icon {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
    }

    .text {
      h4 {
        margin: 0;
        font-size: ${fontSizes.base}px;
        line-height: ${lineHeights.base}px;
        font-weight: 600;
      }
      p {
        margin: 0;
        font-size: ${fontSizes.sm}px;
        line-height: ${lineHeights.sm}px;
        opacity: 0.8;
      }
    }
  }
`;

const ActionButton = styled.button<{ $variant: "check" | "continue" | "next" }>`
  padding: 1rem 3rem;
  border-radius: 1rem;
  border: none;
  font-weight: var(--font-weight-semibold);
  font-size: ${fontSizes.base}px;
  line-height: ${lineHeights.base}px;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  ${(props) =>
    props.$variant === "check" &&
    css`
      background: #5f33e1;
      color: white;
      &:disabled {
        background: #e5e7eb;
        cursor: not-allowed;
      }
      &:hover:not(:disabled) {
        background: #4c29b6;
      }
    `}

  ${(props) =>
    props.$variant === "continue" &&
    css`
      background: #10b981;
      color: white;
      &:hover {
        background: #059669;
      }
    `}

  ${(props) =>
    props.$variant === "next" &&
    css`
      background: #ef4444;
      color: white;
      &:hover {
        background: #dc2626;
      }
    `}
`;

const CompletionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-align: center;
  gap: 2rem;
  animation: fadeIn 0.5s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .icon {
    width: 120px;
    height: 120px;
    background: #5f33e110;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #5f33e1;
  }

  h1 {
    font-size: ${fontSizes["3xl"]}px;
    line-height: ${lineHeights["3xl"]}px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0;
  }
  p {
    font-size: ${fontSizes.base}px;
    line-height: ${lineHeights.base}px;
    color: #666;
    margin: 0;
  }

  .stats-row {
    display: flex;
    gap: 1.5rem;
    margin-top: 1rem;
  }

  .stat-card {
    background: white;
    padding: 1.5rem 2.5rem;
    border-radius: 1.5rem;
    border: 2px solid #f3f4f6;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;

    .label {
      font-size: ${fontSizes.sm}px;
      line-height: ${lineHeights.sm}px;
      color: #999;
      text-transform: uppercase;
    }
    .value {
      font-size: ${fontSizes.xl}px;
      line-height: ${lineHeights.xl}px;
      font-weight: var(--font-weight-bold);
      color: #5f33e1;
    }
  }
`;

const LessonContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [lesson] = useState<Lesson | null>(location.state?.lesson || null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [feedbackStatus, setFeedbackStatus] = useState<
    "correct" | "wrong" | "idle"
  >("idle");
  const [isCompleted, setIsCompleted] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [xpTotal, setXpTotal] = useState(0);

  const currentExercise = useMemo(() => {
    return lesson?.exercises[currentIndex];
  }, [lesson, currentIndex]);

  const progress = useMemo(() => {
    if (!lesson) return 0;
    return (currentIndex / lesson.exercises.length) * 100;
  }, [lesson, currentIndex]);

  const handleCheck = () => {
    if (!currentExercise || !selectedOption) return;

    if (selectedOption === currentExercise.correctAnswer) {
      setFeedbackStatus("correct");
      setCorrectCount((prev) => prev + 1);
      setXpTotal((prev) => prev + (currentExercise.points || 10));
    } else {
      setFeedbackStatus("wrong");
    }
  };

  const handleContinue = () => {
    if (!lesson) return;

    if (currentIndex < lesson.exercises.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setSelectedOption(null);
      setFeedbackStatus("idle");
    } else {
      setIsCompleted(true);
      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
      });
    }
  };

  const handleFinish = () => {
    navigate("/lessons");
  };

  if (!lesson || !currentExercise) {
    return (
      <MobileLayout headerTitle="Lição" headerType="private">
        <div style={{ padding: "16px" }}>
          <p>Lição não encontrada.</p>
          <ActionButton $variant="check" onClick={() => navigate("/lessons")}>
            Voltar
          </ActionButton>
        </div>
      </MobileLayout>
    );
  }

  if (isCompleted) {
    return (
      <MobileLayout headerTitle="Concluído!" headerType="private">
        <div style={{ padding: "16px" }}>
          <CompletionContainer>
            <div className="icon">
              <Award size={64} variant="Bulk" />
            </div>
            <h1>
              {correctCount === lesson.exercises.length
                ? "Espetacular!"
                : "Lição Concluída!"}
            </h1>
            <p>Você completou todos os exercícios deste módulo.</p>

            <div className="stats-row">
              <div className="stat-card">
                <span className="label">Pontos XP</span>
                <span className="value">+{xpTotal}</span>
              </div>
              <div className="stat-card">
                <span className="label">Precisão</span>
                <span className="value">
                  {Math.round((correctCount / lesson.exercises.length) * 100)}%
                </span>
              </div>
            </div>

            <ActionButton
              $variant="continue"
              onClick={handleFinish}
              style={{ marginTop: "2rem" }}
            >
              Finalizar Trilha
            </ActionButton>
          </CompletionContainer>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout headerTitle={lesson.title} headerType="private">
      <div style={{ padding: "0 16px", marginTop: "16px" }}>
        <HeaderWrapper>
          <CloseButton onClick={() => navigate("/lessons")}>
            <CloseCircle size={32} />
          </CloseButton>
          <ProgressTrack>
            <ProgressBar $progress={progress} />
          </ProgressTrack>
          <div
            style={{
              color: "#999",
              fontSize: fontSizes.sm,
              width: "3rem",
            }}
          >
            {currentIndex + 1} / {lesson.exercises.length}
          </div>
        </HeaderWrapper>

        <ExerciseContainer>
          <QuestionBox>
            <h2>{currentExercise.question}</h2>
          </QuestionBox>

          {currentExercise.videoUrl && (
            <MediaWrapper>
              <video
                key={currentExercise.videoUrl}
                src={currentExercise.videoUrl}
                autoPlay
                loop
                muted
                playsInline
              />
            </MediaWrapper>
          )}

          <OptionsGrid>
            {currentExercise.options.map((option) => {
              let status: "selected" | "correct" | "wrong" | "idle" = "idle";
              if (selectedOption === option) status = "selected";

              if (feedbackStatus !== "idle") {
                if (option === currentExercise.correctAnswer)
                  status = "correct";
                else if (selectedOption === option) status = "wrong";
                else status = "idle";
              }

              return (
                <OptionButton
                  key={`${currentExercise.id}-${option}`}
                  $status={status}
                  onClick={() =>
                    feedbackStatus === "idle" && setSelectedOption(option)
                  }
                  disabled={feedbackStatus !== "idle"}
                >
                  {option}
                </OptionButton>
              );
            })}
          </OptionsGrid>
        </ExerciseContainer>
      </div>

      <Footer $status={feedbackStatus}>
        <FooterContent>
          <div className="feedback">
            {feedbackStatus === "correct" && (
              <>
                <div className="icon" style={{ background: "#10B981" }}>
                  <TickCircle size={28} variant="Bold" />
                </div>
                <div className="text" style={{ color: "#065F46" }}>
                  <h4>Excelente!</h4>
                  <p>
                    {currentExercise.explanation || "Você acertou o sinal."}
                  </p>
                </div>
              </>
            )}
            {feedbackStatus === "wrong" && (
              <>
                <div className="icon" style={{ background: "#EF4444" }}>
                  <CloseCircle size={28} variant="Bold" />
                </div>
                <div className="text" style={{ color: "#991B1B" }}>
                  <h4>Ops! Resposta incorreta</h4>
                  <p>
                    A resposta certa era:{" "}
                    <strong>{currentExercise.correctAnswer}</strong>
                  </p>
                </div>
              </>
            )}
          </div>

          {feedbackStatus === "idle" ? (
            <ActionButton
              $variant="check"
              disabled={!selectedOption}
              onClick={handleCheck}
            >
              Verificar
            </ActionButton>
          ) : (
            <ActionButton
              $variant={feedbackStatus === "correct" ? "continue" : "next"}
              onClick={handleContinue}
            >
              Continuar
            </ActionButton>
          )}
        </FooterContent>
      </Footer>

      {/* Padding for fixed footer */}
      <div style={{ height: "140px" }} />
    </MobileLayout>
  );
};

export default LessonContent;
